import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import styled, { injectGlobal } from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line import/no-extraneous-dependencies
import SimpleMDE from 'react-simplemde-editor';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'easymde/dist/easymde.min.css';

// Inject EasyMDE global overrides (styled-components v3); assignment satisfies no-unused-expressions
// eslint-disable-next-line no-unused-vars
const injectedEasyMdeStyles = injectGlobal`
  .EasyMDEContainer {
    font-family: "Petasense Open Sans", sans-serif;
  }

  .EasyMDEContainer .CodeMirror {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 12px;
    line-height: 1.5;
    border: none;
    border-radius: 0;
    height: auto;
    color: inherit;
    background: transparent;
    min-height: 180px;
  }

  .EasyMDEContainer .CodeMirror-scroll {
    min-height: 180px;
    max-height: 350px;
    overflow-y: auto !important;
  }

  .EasyMDEContainer .editor-toolbar {
    border-bottom: 1px solid #e0e0e0;
    background-color: #f5f5f5;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    padding: 4px 6px;
    opacity: 1;
  }

  .EasyMDEContainer .editor-toolbar button {
    color: #555;
    border: none;
    border-radius: 3px;
    width: 28px;
    height: 28px;
    padding: 0;
  }

  .EasyMDEContainer .editor-toolbar button:hover,
  .EasyMDEContainer .editor-toolbar button.active {
    background-color: #e0e0e0;
    border-color: transparent;
  }

  .EasyMDEContainer .editor-toolbar i.separator {
    border-color: #ccc;
  }

  /* Preview pane */
  .EasyMDEContainer .editor-preview {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 12px;
    line-height: 1.5;
    background: #fff;
    padding: 10px;
    box-sizing: border-box;
  }

  .EasyMDEContainer .editor-preview p,
  .EasyMDEContainer .editor-preview li,
  .EasyMDEContainer .editor-preview ul,
  .EasyMDEContainer .editor-preview ol {
    font-size: 12px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  }

  .EasyMDEContainer .editor-preview h1 {
    font-size: 16px;
    font-weight: 600;
    margin-top: 12px;
    margin-bottom: 6px;
  }

  .EasyMDEContainer .editor-preview h2 {
    font-size: 14px;
    font-weight: 600;
    margin-top: 10px;
    margin-bottom: 5px;
  }

  .EasyMDEContainer .editor-preview h3 {
    font-size: 13px;
    font-weight: 600;
    margin-top: 8px;
    margin-bottom: 4px;
  }

  .EasyMDEContainer .editor-preview p {
    margin-bottom: 6px;
  }

  .EasyMDEContainer .editor-preview ul,
  .EasyMDEContainer .editor-preview ol {
    padding-left: 20px;
    margin-bottom: 6px;
  }

  .EasyMDEContainer .editor-preview li {
    margin-bottom: 3px;
  }

  .EasyMDEContainer .editor-preview hr {
    margin: 10px 0;
    border: none;
    border-top: 1px solid #e0e0e0;
  }

  .EasyMDEContainer .CodeMirror-focused .CodeMirror-cursor {
    border-left-color: #333;
  }

  /* When preview is active, hide CodeMirror scroll area (we toggle .ps-mde-preview-active via JS) */
  .ps-mde-preview-active .CodeMirror-scroll,
  .ps-mde-preview-active .CodeMirror-scrollbar-filler,
  .ps-mde-preview-active .CodeMirror-gutter-filler {
    display: none !important;
  }

  /* Heading sizes in edit mode */
  .EasyMDEContainer .CodeMirror .cm-header-1 {
    font-size: 16px;
    line-height: 1.5;
  }

  .EasyMDEContainer .CodeMirror .cm-header-2 {
    font-size: 14px;
    line-height: 1.5;
  }

  .EasyMDEContainer .CodeMirror .cm-header-3,
  .EasyMDEContainer .CodeMirror .cm-header-4,
  .EasyMDEContainer .CodeMirror .cm-header-5,
  .EasyMDEContainer .CodeMirror .cm-header-6 {
    font-size: 13px;
    line-height: 1.5;
  }

  /* Disabled state */
  .EasyMDEContainer.disabled-editor .editor-toolbar {
    pointer-events: none;
    opacity: 0.5;
  }

  .EasyMDEContainer.disabled-editor .CodeMirror {
    pointer-events: none;
    opacity: 0.7;
    background-color: #f9f9f9;
  }
`;

const EditorContainer = styled.div`
  margin-bottom: ${props => props.marginBottom || '0'};

  .EasyMDEContainer {
    border: 1px solid ${props => props.theme.colors.grey};
    border-radius: 4px;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    overflow: hidden;
  }

  .EasyMDEContainer .CodeMirror {
    border: none;
    color: ${props => props.theme.colors.black};
  }

  .EasyMDEContainer .editor-toolbar {
    background-color: ${props => props.theme.colors.greyXXXL || '#f5f5f5'};
    border-bottom: 1px solid ${props => props.theme.colors.grey};
    border-top: none;
  }

  .EasyMDEContainer .editor-toolbar button {
    color: ${props => props.theme.colors.greyXD};
  }

  .EasyMDEContainer .editor-toolbar button:hover,
  .EasyMDEContainer .editor-toolbar button.active {
    background-color: ${props => props.theme.colors.greyXL || '#e0e0e0'};
  }
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  font-family: ${props => props.theme.fonts.lightLabel};
  color: ${props => props.theme.colors.black};
  margin-bottom: 8px;
`;

const HelperText = styled.p`
  font-size: 10px;
  font-family: "Petasense Open Sans", sans-serif;
  color: ${props => props.theme.colors.greyXD};
  margin-top: 5px;
`;

const TOOLBAR_TOOLTIP_KEYS = {
  bold: 'common.richTextEditor.tooltips.bold',
  italic: 'common.richTextEditor.tooltips.italic',
  'heading-1': 'common.richTextEditor.tooltips.heading1',
  'heading-2': 'common.richTextEditor.tooltips.heading2',
  'heading-3': 'common.richTextEditor.tooltips.heading3',
  'unordered-list': 'common.richTextEditor.tooltips.unorderedList',
  'ordered-list': 'common.richTextEditor.tooltips.orderedList',
  'horizontal-rule': 'common.richTextEditor.tooltips.horizontalRule',
  preview: 'common.richTextEditor.tooltips.preview',
};

const applyToolbarTooltips = (container, t) => {
  if (!container) return;
  const toolbar = container.querySelector('.editor-toolbar');
  if (!toolbar) return;

  Object.entries(TOOLBAR_TOOLTIP_KEYS).forEach(([className, key]) => {
    const btn = toolbar.querySelector(`button.${className}`);
    if (!btn) return;
    const shortcutMatch = (btn.title || '').match(/(\([^)]+\))$/);
    const shortcut = shortcutMatch ? ` ${shortcutMatch[1]}` : '';
    const label = t(key);
    btn.title = `${label}${shortcut}`;
    btn.setAttribute('aria-label', label);
  });
};

const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  label,
  disabled,
  marginBottom
}) => {
  const { t } = useTranslation();
  const mdeInstanceRef = useRef(null);
  const containerRef = useRef(null);
  const [mdeReady, setMdeReady] = useState(false);

  const setPreviewClass = useCallback((mde) => {
    if (!containerRef.current) return;
    const easyMdeContainer = containerRef.current.querySelector('.EasyMDEContainer');
    if (!easyMdeContainer) return;
    if (mde.isPreviewActive()) {
      easyMdeContainer.classList.add('ps-mde-preview-active');
    } else {
      easyMdeContainer.classList.remove('ps-mde-preview-active');
    }
  }, []);

  const handleMdeInstance = useCallback((instance) => {
    mdeInstanceRef.current = instance;
    setMdeReady(true);
  }, []);

  useEffect(() => {
    if (!mdeReady || disabled) return;
    applyToolbarTooltips(containerRef.current, t);
  }, [mdeReady, disabled, t]);

  useEffect(() => {
    const mde = mdeInstanceRef.current;
    if (!mde) return;

    if (value && !mde.isPreviewActive()) {
      mde.togglePreview();
    }
    setPreviewClass(mde);

    const originalToggle = mde.togglePreview.bind(mde);
    mde.togglePreview = (...args) => {
      originalToggle(...args);
      setPreviewClass(mde);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = useCallback(
    (newValue) => {
      onChange({ target: { value: newValue } });
    },
    [onChange]
  );

  const options = useMemo(
    () => ({
      placeholder: placeholder || t('common.richTextEditor.placeholder'),
      spellChecker: false,
      autofocus: false,
      status: false,
      toolbar: disabled
        ? false
        : [
          'bold',
          'italic',
          '|',
          'heading-1',
          'heading-2',
          'heading-3',
          '|',
          'unordered-list',
          'ordered-list',
          'horizontal-rule',
          '|',
          'preview'
        ]
    }),
    [placeholder, disabled, t]
  );

  return (
    <div ref={containerRef}>
      <EditorContainer marginBottom={marginBottom}>
        {label && <Label>{label}</Label>}
        <SimpleMDE
          value={value}
          onChange={disabled ? () => {} : handleChange}
          options={options}
          className={disabled ? 'disabled-editor' : ''}
          getMdeInstance={handleMdeInstance}
        />
        {!disabled && (
          <HelperText>
            {t('common.richTextEditor.markdownHint')}
          </HelperText>
        )}
      </EditorContainer>
    </div>
  );
};

RichTextEditor.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  marginBottom: PropTypes.string
};

RichTextEditor.defaultProps = {
  placeholder: undefined,
  label: undefined,
  disabled: false,
  marginBottom: '0'
};

export default RichTextEditor;
